import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect } from "react";

type UiState = { [id: string]: unknown };
const initialState: UiState = {};

const uiStateSlice = createSlice({
    name: "uiState",
    initialState,
    reducers: {
        setUiProperty: function <T>(
            state: UiState,
            action: PayloadAction<{ id: string; value: T }>,
        ) {
            state[action.payload.id] = action.payload.value;
        },
    },
});

export const { setUiProperty } = uiStateSlice.actions;
export function getUiProperty<T>(id: string) {
    return (state: { uiState: UiState }) => state.uiState[id] as T | undefined;
}

export function useUiProperty<T>(
    id: string,
    defaultValue?: T,
): [T extends undefined ? T | undefined : T, (value: T) => void] {
    const dispatch = useDispatch();

    let currentProp = useSelector(getUiProperty<T>(id));

    useEffect(() => {
        if (typeof currentProp === "undefined") {
            dispatch(
                setUiProperty({
                    id,
                    value: defaultValue,
                }),
            );
        }
    }, [currentProp, defaultValue, dispatch, id]);

    if (typeof currentProp === "undefined") {
        currentProp = defaultValue;
    }

    const setValue = useCallback(
        (value: T) =>
            dispatch(
                setUiProperty({
                    id,
                    value,
                }),
            ),
        [dispatch, id],
    );

    return [currentProp as T extends undefined ? T | undefined : T, setValue];
}

type UiStateWithPersist = {
    uiState: { _persist?: { rehydrated?: boolean } };
};

export function useUiStateRehydrated(): boolean {
    return useSelector(
        (state: UiStateWithPersist) =>
            state.uiState._persist?.rehydrated ?? false,
    );
}

export default uiStateSlice.reducer;
